import axios from "axios";

const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Call the API on the SAME origin the app is served from so the auth cookie is
// first-party (works on custom domains + strict browsers like Brave, and avoids
// cross-site CORS). Fall back to the env URL for localhost dev or SSR.
function resolveBackendBase() {
  try {
    if (typeof window !== "undefined" && window.location) {
      const host = window.location.hostname;
      const envHost = ENV_BACKEND_URL ? new URL(ENV_BACKEND_URL).hostname : "";
      if (host === "localhost" || host === "127.0.0.1") return ENV_BACKEND_URL;
      // Served from a domain that isn't the baked backend host (e.g. a custom
      // domain) -> use same-origin; the ingress routes /api to the backend.
      if (host !== envHost) return window.location.origin;
    }
  } catch (e) { /* fall through to env */ }
  return ENV_BACKEND_URL;
}

const BACKEND_URL = resolveBackendBase();
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// CSRF double-submit: echo the first-party csrf_token cookie in a header on every
// state-changing request (covers JSON + multipart uploads, since all go through `api`).
function readCookie(name) {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}
api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    const token = readCookie("csrf_token");
    if (token) {
      config.headers = config.headers || {};
      config.headers["X-CSRF-Token"] = token;
    }
  }
  return config;
});

// Transparent recovery from an expired 15-min access token: on 401 we try ONE
// silent /auth/refresh (valid 7-day refresh cookie) and retry the original request.
// If refresh also fails, the session is genuinely over -> signal the app so it can
// preserve any draft and prompt sign-in. A 403 CSRF (e.g. missing csrf cookie after
// an old tab) is recovered once by re-fetching /auth/me (which re-sets the cookie).
function isAuthLifecyclePath(url = "") {
  return ["/auth/login", "/auth/register", "/auth/refresh", "/auth/forgot-password",
          "/auth/reset-password", "/auth/me"].some((p) => url.includes(p));
}
let refreshPromise = null;
api.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const { response, config } = error;
    if (!response || !config) return Promise.reject(error);
    const url = config.url || "";

    if (response.status === 401 && !config._authRetry && !isAuthLifecyclePath(url)) {
      config._authRetry = true;
      try {
        refreshPromise = refreshPromise || api.post("/auth/refresh");
        await refreshPromise;
        refreshPromise = null;
        return api(config);
      } catch (e) {
        refreshPromise = null;
        error.sessionExpired = true;
        if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("si:session-expired"));
        return Promise.reject(error);
      }
    }

    if (response.status === 403 && !config._csrfRetry &&
        typeof response.data?.detail === "string" && response.data.detail.toLowerCase().includes("csrf")) {
      config._csrfRetry = true;
      try { await api.get("/auth/me"); return api(config); } catch (e) { /* fall through */ }
    }

    return Promise.reject(error);
  }
);

export function apiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  return JSON.stringify(detail);
}
